/**
 * MessageThread — shared message-bubble rendering for every support surface:
 * the widget's ThreadView (requester POV) and the org-admin/platform queue
 * shell (handler POV). Both need identical bubble/divider/attachment
 * rendering with only the "who is the other party" label differing, so this
 * is the one place that logic lives.
 *
 * Image attachments render inline as a thumbnail that opens full-size in a
 * lightbox — screenshots are the overwhelmingly common support attachment, and
 * making the handler download a file just to see what the user is describing
 * is friction on every single ticket. Non-image attachments keep the download
 * button. Both go through the same authorized blob fetch: the stream endpoint
 * is JwtAuthGuard-protected, so a plain `<img src>` pointed at it would 401.
 */

import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Loader2, Paperclip } from 'lucide-react';
import type { SupportAttachment, SupportMessage } from '@upskill/shared';
import { formatRelativeTime } from '../../utils/dateUtils';
import { fetchSupportAttachmentUrl } from '../../services/api/supportAttachmentDownload';
import { Modal } from '../ui/Modal';

const Divider = styled.div`
  text-align: center;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  margin: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
`;

const MessageGroup = styled.div<{ $mine: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $mine }) => ($mine ? 'flex-end' : 'flex-start')};
  margin: 0 ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[2]};
`;

const MessageMeta = styled.div`
  font-size: 10.5px;
  color: ${({ theme }) => theme.colors.text.light};
  margin-bottom: 2px;
`;

const Bubble = styled.div<{ $mine: boolean; $maxWidth: string }>`
  max-width: ${({ $maxWidth }) => $maxWidth};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  overflow-wrap: anywhere;
  white-space: pre-wrap;
  background: ${({ theme, $mine }) =>
    $mine ? theme.colors.backgrounds.light : theme.colors.backgrounds.white};
  border: 1px solid
    ${({ theme, $mine }) => ($mine ? theme.colors.primary.lightSky : theme.colors.borders.light)};
`;

const AttachmentRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const AttachmentButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.primary.base};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.7;
  }

  svg.spin {
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ThumbButton = styled.button`
  display: block;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  cursor: pointer;
  overflow: hidden;
  line-height: 0;
  max-width: 100%;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }

  img {
    display: block;
    max-width: 100%;
    /* Bounded so a tall screenshot can't push the rest of the thread off-screen;
       the lightbox is where it gets read properly. */
    max-height: 180px;
    object-fit: contain;
  }
`;

const ThumbPlaceholder = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};

  svg.spin {
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ThumbCaption = styled.div`
  font-size: 10.5px;
  color: ${({ theme }) => theme.colors.text.light};
  overflow-wrap: anywhere;
`;

const LightboxImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 70vh;
  margin: 0 auto;
  object-fit: contain;
`;

/** True for attachments the browser can render inline as an image. */
function isPreviewableImage(contentType: string): boolean {
  return /^image\/(png|jpeg|jpg|gif|webp|bmp|avif)$/i.test(contentType);
}

/**
 * Inline image attachment: an authorized blob fetch rendered as a bounded
 * thumbnail, opening full-size in a lightbox on click. The object URL is owned
 * here and revoked on unmount — leaking one pins the whole image in memory for
 * the life of the tab, and a busy queue scrolls through a lot of screenshots.
 */
function AttachmentImagePreview({
  fileAssetId,
  filename,
  onDownload,
}: {
  fileAssetId: string;
  filename: string;
  onDownload: (fileAssetId: string, filename: string) => Promise<void>;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    fetchSupportAttachmentUrl(fileAssetId)
      .then((next) => {
        objectUrl = next;
        if (cancelled) {
          // Unmounted mid-flight — nothing will render it, so release it now.
          URL.revokeObjectURL(next);
          return;
        }
        setUrl(next);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileAssetId]);

  // A failed preview must still leave the file reachable — fall back to the
  // download control rather than stranding the attachment behind a broken image.
  if (failed) {
    return (
      <AttachmentDownload
        fileAssetId={fileAssetId}
        filename={filename}
        onDownload={onDownload}
      />
    );
  }

  if (!url) {
    return (
      <ThumbPlaceholder>
        <Loader2 size={10} className="spin" /> {filename}
      </ThumbPlaceholder>
    );
  }

  return (
    <div>
      <ThumbButton
        type="button"
        onClick={() => setLightboxOpen(true)}
        aria-label={`View ${filename} full size`}
      >
        <img src={url} alt={filename} onError={() => setFailed(true)} />
      </ThumbButton>
      <ThumbCaption>{filename}</ThumbCaption>

      <Modal
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        title={filename}
        size="xl"
      >
        <LightboxImage src={url} alt={filename} />
      </Modal>
    </div>
  );
}

/** Routes an attachment to the inline preview or the download control. */
function AttachmentItem({
  attachment,
  onDownload,
}: {
  attachment: SupportAttachment;
  onDownload: (fileAssetId: string, filename: string) => Promise<void>;
}) {
  return isPreviewableImage(attachment.contentType) ? (
    <AttachmentImagePreview
      fileAssetId={attachment.fileAssetId}
      filename={attachment.filename}
      onDownload={onDownload}
    />
  ) : (
    <AttachmentDownload
      fileAssetId={attachment.fileAssetId}
      filename={attachment.filename}
      onDownload={onDownload}
    />
  );
}

/**
 * A single attachment's download control. The stream endpoint is
 * JwtAuthGuard-protected, so this fetches through apiClient (which attaches
 * the bearer token) rather than rendering a plain href — a browser-initiated
 * request to that URL would 401.
 */
function AttachmentDownload({
  fileAssetId,
  filename,
  onDownload,
}: {
  fileAssetId: string;
  filename: string;
  onDownload: (fileAssetId: string, filename: string) => Promise<void>;
}) {
  const [downloading, setDownloading] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleClick = async () => {
    setDownloading(true);
    setFailed(false);
    try {
      await onDownload(fileAssetId, filename);
    } catch {
      setFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <AttachmentButton type="button" onClick={handleClick} disabled={downloading}>
      {downloading ? <Loader2 size={10} className="spin" /> : <Paperclip size={10} />}
      {filename}
      {failed && ' — download failed'}
    </AttachmentButton>
  );
}

/** Resolves the label shown for a non-mine message's author. */
function resolveAuthorLabel(
  message: SupportMessage,
  otherPartyLabel: string,
): string {
  if (message.author) return `${message.author.firstName} ${message.author.lastName}`;
  // authorId null on a USER_MESSAGE/HANDLER_MESSAGE means the author was hard-deleted
  // (support_messages.author_id is ON DELETE SET NULL) — distinct from "not hydrated".
  if (message.authorId === null) return 'Former team member';
  return otherPartyLabel;
}

export interface MessageThreadProps {
  messages: SupportMessage[];
  currentUserId: string | undefined;
  /** Fallback name for a non-mine message whose `author` wasn't hydrated but `authorId` is set. */
  otherPartyLabel: string;
  onDownloadAttachment: (fileAssetId: string, filename: string) => Promise<void>;
  /** Bubble max-width — the widget's narrow panel and the queue's wide detail pane read differently. */
  maxBubbleWidth?: string;
}

export function MessageThread({
  messages,
  currentUserId,
  otherPartyLabel,
  onDownloadAttachment,
  maxBubbleWidth = '85%',
}: MessageThreadProps) {
  // Re-renders (filter/pagination/modal-toggle changes elsewhere on the page)
  // shouldn't rebuild every bubble when the underlying messages haven't changed.
  const rendered = useMemo(
    () =>
      messages.map((m) => {
        if (m.kind === 'SYSTEM_EVENT') {
          return <Divider key={m.id}>{m.body}</Divider>;
        }
        const mine = m.authorId === currentUserId;
        return (
          <MessageGroup key={m.id} $mine={mine}>
            <MessageMeta>
              {mine ? 'You' : resolveAuthorLabel(m, otherPartyLabel)} · {formatRelativeTime(m.createdAt)}
            </MessageMeta>
            <Bubble $mine={mine} $maxWidth={maxBubbleWidth}>
              {m.body}
              {m.attachments.length > 0 && (
                <AttachmentRow>
                  {m.attachments.map((a) => (
                    <AttachmentItem
                      key={a.fileAssetId}
                      attachment={a}
                      onDownload={onDownloadAttachment}
                    />
                  ))}
                </AttachmentRow>
              )}
            </Bubble>
          </MessageGroup>
        );
      }),
    [messages, currentUserId, otherPartyLabel, onDownloadAttachment, maxBubbleWidth],
  );

  return <>{rendered}</>;
}

export default MessageThread;
